/**
 * 报名确认页的三块内容：信息汇总 / 承诺勾选 / 手写签名
 * -----------------------------------------------------------------------------
 * 2026-10-21 用户裁决 —— 确认页只做「核对 + 签署」：
 *   - 教材邮寄地址 → 第 1 步（报名阶段只填地址本身，收件人 / 手机号在领取教材时补）
 *   - 费用明细     → 缴费功能（报名是纯信息填写，不在表里出现金额）
 *   - 承诺 + 签名  → 本页，签完就地提交（原来是独立的「材料与承诺」第 2 步）
 *
 * 为什么汇总要**逐项列全**（含姓名 / 身份证 / 手机号）：
 *   这一页是学员唯一能回头核对全部填写内容的地方 —— 前三步各看各的，出了页面再看
 *   只能进「报名详情」，而那时单据已经提交、字段可能已锁定。汇总不全，打回重提
 *   就会变成常态。
 *
 * 为什么承诺 / 签名单独做组件、不复用 `FormRenderer`：
 *   它们的位置已经从「材料组」挪到了确认页（字段分组还是后台的 `extra` 组），
 *   按组渲染会把整组一起搬过来。这里按**字段 key / 类型**取，位置与分组解耦。
 */
import type { AnswerError, FormFieldDef } from '@enroll/shared';
import { Checkbox } from 'antd-mobile';
import { formatAnswerText, photoRowGroupIndex } from './EnrollConfirm.logic';
import type { Answers, FormFieldGroupView } from './FormRenderer/logic';
import { SignatureCanvas } from './FormRenderer/SignatureCanvas';
import { Card, KV, KVImage } from './layout';

/**
 * 证件照缩略图行。
 *
 * 为什么给缩略图而不是「已生成」三个字：这一页是提交前最后一次核对，
 * 「生成得对不对」只能看出来，看不出来就等于没得核对（头顶有没有出框、
 * 是不是白底、脸有没有偏，三件事都得看图才知道）。
 * 照片是 295×413 的一寸规格，缩到 44×56 只是索引，点开才是核对。
 */
function PhotoRow({ url, lined }: { url: string; lined: boolean }) {
  return <KVImage label="职业证件照" url={url} alt="职业证件照" lined={lined} />;
}

/** 信息汇总：按后台分组分卡，逐项列出「字段名 → 值」 */
export function AnswerSummary({
  groups,
  answers,
  photoUrl,
}: {
  groups: readonly FormFieldGroupView[];
  answers: Answers;
  /**
   * 职业证件照的短期签名链接；`null` = 没有可展示的图（未生成 / 链接取不到）。
   *
   * 为 `null` 时**整行不渲染**：确认页一定是在核验通过之后才到得了，照片本该存在，
   * 取不到只可能是链接过期 —— 那属于「看不见」而不是「没做」，写一行「未生成」
   * 反而会让学员以为要重拍。
   */
  photoUrl?: string | null;
}) {
  // 目标分组：找不到时 `photoRowGroupIndex` 会兜到第一组
  const photoIndex = photoRowGroupIndex(groups);
  return (
    <>
      {groups.map((group, index) => (
        <Card key={group.key} title={group.label} tight>
          {/*
            每行之间加浅色分割线（2026-10-21 用户反馈）。
            汇总一屏二三十行，光靠 6px 行距时「这一行的值」与「下一行的键」会粘成
            一片 —— 核对时串行比看不清更麻烦，而这一页的全部意义就是逐项核对。
            线由 `.kv--lined` 的相邻选择器画在**两行之间**，所以首行不带线（它上面
            就是卡片标题，再加一条会把标题和内容切开）。
          */}
          {group.fields.map((field) => (
            <KV
              key={field.key}
              lined
              label={field.label}
              value={formatAnswerText(field, answers[field.key])}
            />
          ))}
          {photoUrl && index === photoIndex ? <PhotoRow url={photoUrl} lined /> : null}
        </Card>
      ))}
    </>
  );
}

/**
 * 承诺勾选。
 *
 * 值就是表单字段本身的值（`string[]`），不另起一份状态 —— 否则「界面勾了、提交时
 * 服务端说没勾」这种错位迟早出现。多选项的文案完全来自后台配置，前端不写死条文。
 */
export function PromiseList({
  fields,
  answers,
  onChange,
  errors,
}: {
  fields: readonly FormFieldDef[];
  answers: Answers;
  onChange: (next: Answers) => void;
  errors: readonly AnswerError[];
}) {
  const errorOf = (key: string) => errors.find((item) => item.key === key)?.message;
  return (
    <>
      {fields.map((field) => {
        const picked = Array.isArray(answers[field.key]) ? (answers[field.key] as string[]) : [];
        const message = errorOf(field.key);
        return (
          <Card key={field.key} title={field.label}>
            <Checkbox.Group
              value={picked}
              onChange={(next) => onChange({ ...answers, [field.key]: next })}
            >
              <div className="choice-list">
                {field.options?.map((option) => (
                  <Checkbox key={option.value} value={option.value}>
                    <span>{option.label}</span>
                  </Checkbox>
                ))}
              </div>
            </Checkbox.Group>
            {message ? <div className="field-error">{message}</div> : null}
          </Card>
        );
      })}
    </>
  );
}

/**
 * 手写签名。
 *
 * 签名图本身就是**承诺的载体**，所以它和勾选项同屏；签在上传件旁边（旧版第 2 步）
 * 会让「签的是材料」和「签的是这份报名表」两种理解同时成立，确认页只有一种。
 *
 * `signatureUrl` 是**服务端下发的存档签名图**（`RegistrationDetail.signatureUrls`）。
 * 没有它，答案里的值只是一串 `assetId`，切个步骤回来（组件卸载重挂）画布内容就没了，
 * 卡片上只剩一句「已签名」—— 而这一页的全部意义就是核对，看不出来等于没核对。
 */
export function SignatureBlock({
  field,
  answers,
  signatureUrl,
  registrationId,
  onChange,
  onBlur,
  errors,
}: {
  field: FormFieldDef;
  answers: Answers;
  signatureUrl?: string | null;
  /** 归属的报名单：提交时服务端据此删掉被重新签署替换掉的旧签名 */
  registrationId?: string;
  onChange: (next: Answers) => void;
  onBlur: (field: FormFieldDef, next?: unknown) => void;
  errors: readonly AnswerError[];
}) {
  const message = errors.find((item) => item.key === field.key)?.message;
  return (
    <Card title={field.label}>
      <p className="field__help">
        {field.placeholder ?? '请在方框内手写签名'}
        {'（签名表示你已核对上方全部信息，且承诺内容属实）'}
      </p>
      <SignatureCanvas
        value={answers[field.key]}
        previewUrl={signatureUrl ?? null}
        disabled={false}
        registrationId={registrationId}
        onChange={(next) => onChange({ ...answers, [field.key]: next })}
        onBlur={(next) => onBlur(field, next)}
      />
      {message ? <div className="field-error">{message}</div> : null}
    </Card>
  );
}
